/**
 * This is an GUI object for the pages showing issues and workorders on a map.
 * All public properties can be set before the init() method is invoked.
 * @author Pål Gjerde Gammelsæter <pgg@adcom.no>
 */
class IssueOrderMapGUI {

    /** The ID selector for container of the map (must not contain "#") */
    public selector:string = "map-container";

    /**
     * The selected position in map
     */
    public position:{lat:number, lng:number};

    /** The selected zoom level in the map */
    public zoom:number;

    /**
     * An array of objects for the map (orders and issues)
     */
    public objects:IssueOrOrderInMap[];

    /**
     * The URL for getting objects
     */
    public getObjectsUrl:string;

    /** The path to icons */
    public iconPath:string;

    /**
     * Base URL for viewing issues (add id argument)
     */
    public baseUrlIssues;
    /**
     * Base URL for viewing orders (ddd id argument)
     */
    public baseUrlOrders;


    // ----------------------
    // Private variabler
    // ----------------------

    public attribution = { attribution: "<a href=\"https://www.kartverket.no/\">Kartverket</a>" };

    /**
     * The URL given to the leaflet library to tell it where to get the map tiles.
     * Set in init()
     */
    private url:string;

    /** The Map object from leaflet */
    private map;

    /** A list of markers showed on the map */
    private markerGroup;

    private iconGreen;
    private iconBlue;
    private iconGps;

    /**
     * This is the flag when we are temporarily disabling the moveend event.
     * This flag is used by the moveend event and controlled by the
     * popupopen event.
     */
    private dontTriggerMoveendEvent = false;

    private config:MapConfig;

    /**
     *
     * @param config The configuration object
     */
    public constructor(config:MapConfig) {
        this.config = config;
    }


    /**
     * Run this function to initialize the map after you have set the object
     */
    public init() {
        this.initCssStylesForPopup();
        this.initIcons();

        let options = {
            attributionControl: false,
            zoomControl: true,
            doubleClickZoom: false,
            keyboard: false,
            //mouseWheelZoom: false,
            zoomAnimation: false,
            boxZoom: false,
            dragging: true,
            scrollWheelZoom: true,
            tap: false,
        };

        this.map = L.map(this.selector, options);

        // Moveend event is triggered on zoom and drag also
        let self = this; // to have "this" available in the event
        this.map.on("moveend", function(event) {
            if (!self.dontTriggerMoveendEvent) {
                self.updateUrl();
                self.getObjects(); // remove markers and add new ones
            }
        });

        // When map shows popup, then deactivate the moveend event temporarily.
        // The reason is that popup triggers a move when there is not enough space
        // for the popup in the map, and the map moves a bit.
        this.map.on("autopanstart", function(event) {
            self.dontTriggerMoveendEvent = true;
        });
        this.map.on("popupclose", function(event) {
            self.dontTriggerMoveendEvent = false;
        });

        // This code was used to set protocol as the same as ELA Mobil was running.
        // On webdev it was run as http, one producetion as https.
        //let protocol = location.protocol; // "http:" or "https:"
        this.url = this.config.protocol+"//"+this.config.host+this.config.path;

        L.tileLayer(this.url, this.attribution).addTo(this.map);
    }

    /**
     * Add style tag to the DOM for styling popups
     */
    private initCssStylesForPopup() {
        let comment = "\n<!-- CSS for popup in the map -->";
        let style = "\n<style>div.popup span.caption { font-weight:bold; } "+
                    "div.popup span.id { text-decoration: underline; }";
        $("body").append(comment+style);
    }

    /**
     * Initialize the icons
     */
    private initIcons() {
        //this.markerGroup = L.featureGroup(); // Group for markers
        this.markerGroup = [];

        // Define general icon settings (shadow removed when new icon introduced)
        let iconOptions = {
            options: {
                iconUrl: this.iconPath+"/pin_blaa.png",
                //shadowUrl: this.iconPath+"/leaflet-default-marker-icon-shadow.png",
                iconSize: [28, 41],
                //shadowSize: [41, 41],
                iconAnchor: [14, 40],
                //shadowAnchor: [12, 40],
                popupAnchor: [0, -40]
            }
        };
        let myIconClass = L.Icon.extend(iconOptions);

        // Set up blue icon
        this.iconBlue = new myIconClass();

        // Set up green icon
        let greenIconOptions = this.cloneObject(iconOptions);
        greenIconOptions.options.iconUrl = this.iconPath+"/pin_gron.png";
        let myGreenIconClass = L.Icon.extend(greenIconOptions);
        this.iconGreen = new myGreenIconClass();

        // The GPS position icon
        let gpsIconClass = L.Icon.extend({
           options: {
               iconUrl: this.iconPath+"/gps.png",
               iconSize: [36,60],
               iconAnchor: [18, 50],
               popupAnchor: [0, -16]
           }
        });

        this.iconGps = new gpsIconClass();
    }

    /**
     * Clone the given object
     * @param {object} obj
     * @return {object}
     */
    private cloneObject(obj) {
        // Thanks to this answer:
        // https://stackoverflow.com/a/10869248
        return JSON.parse(JSON.stringify(obj));
    }


    /**
     * Set the view on the map to given position and zoom level
     * @param position
     * @param zoom
     */
    public setView(position:{lat:number, lng:number}, zoom:number) {
        let options = {
            attributionControl: false,
            zoomControl: false,
            doubleClickZoom: false,
            keyboard: false,
            //mouseWheelZoom: false,
            zoomAnimation: false,
            boxZoom: false,
            dragging: false,
            scrollWheelZoom: false,
            tap: false,
        };

        this.map.setView([position.lat, position.lng], zoom);
        this.map.invalidateSize();
        this.getObjects();
        this.setGpsPosition(position);
    }

    /**
     * Set the GPS current position marker on the map
     * @param position
     */
    private setGpsPosition(position:{lat:number, lng:number}) {
        // Add GPS position marker
        let marker = L.marker(position, {icon: this.iconGps }).addTo(this.map);
        this.markerGroup.push(marker);
    }


    /**
     * Get all orders and issues and show them on the map
     */
    private getObjects() {
        let bounds = this.map.getBounds();
        let self = this; // to have the class available to the success function
        $.ajax({
            url: this.getObjectsUrl,
            method: "GET",
            data: {
                swlat: bounds.getSouth(),
                swlng: bounds.getWest(),
                nelat: bounds.getNorth(),
                nelng: bounds.getEast()
            },
            success: function(data) {
                self.removeMarkers();
                let data2:IssueOrOrderInMap[] = JSON.parse(data);
                for (let i=0; i<data2.length; i++) {
                    self.addMarker(data2[i]);
                }
            },
            error: function() {
                console.log("error getting objects");
            }
        });
    }

    /**
     * Remove all markers from the map
     */
    private removeMarkers() {
        for (let i=0; i<this.markerGroup.length; i++) {
            this.markerGroup[i].remove();
        }
        this.markerGroup = [];
    }

    /**
     * Update the URL in the web browser without redirecting the user.
     * Using this URL when sending users to other URLs so that their
     * back button will return to the same map bounds as they came from.
     */
    private updateUrl() {
        //let currentUrl = history.state.url;
        let currentUrl = window.location.href;
        let parsedUrl = new URL(currentUrl);
        let bounds = this.map.getBounds();
        // searchparams documentation:
        // https://developer.mozilla.org/en-US/docs/Web/API/URL
        parsedUrl.searchParams.set("swlat", bounds.getSouth());
        parsedUrl.searchParams.set("swlng", bounds.getWest());
        parsedUrl.searchParams.set("nelat", bounds.getNorth());
        parsedUrl.searchParams.set("nelng", bounds.getEast());

        //let newUrl = parsedUrl.href;

        // Got this code from here:
        // https://stackoverflow.com/a/8560766
        history.pushState({}, "URL Rewrite Example", parsedUrl.href);
    }

    /**
     * Add marker to the map for given issue or workorder
     * @param {IssueOrOrderInMap} item
     */
    private addMarker(item:IssueOrOrderInMap) {
        let position = L.latLng(item.lat, item.lng);
        let marker;
        // The base for the popup
        let popupContent = "<div class=\"popup\"><span class=\"id\"><span class=\"caption\">{NumberCaption}: </span> "+item.id+"</span><br/>"+
            "<span class=\"caption\">{TypeCaption}: </span>"+item.type+"<br/><span class=\"caption\">Emne: </span>"+item.name+"<br/>"+
            "<span class=\"caption\">Beskrivelse: </span>"+item.desc+"<br/><br/>"+
            "<a href=\"{BaseLink}&id="+item.id+"\">{LinkCaption}</a></div>";
        // The replace strings in the popup (to adapt to issue and order)
        let captionMap = {
            "{NumberCaption}": "Ordrenummer",
            "{TypeCaption}": "Ordretype",
            "{LinkCaption}": "Åpne ordre",
            "{BaseLink}": this.baseUrlOrders
        };
        let selectedIcon = this.iconGreen;

        // If current item/marker is for issue, then change some values
        if (!item.order) {
            // Adapt the captions in the popup to issue
            captionMap["{NumberCaption}"] = "Saknummer";
            captionMap["{TypeCaption}"] = "Sakstype";
            captionMap["{LinkCaption}"] = "Åpne sak";
            captionMap["{BaseLink}"] = this.baseUrlIssues;
            selectedIcon = this.iconBlue;
        }

        // Replace the variables in the popup beforing showing it
        popupContent = this.replaceStrings(popupContent, captionMap);

        marker = L.marker(position, {icon: selectedIcon }).
            addTo(this.map).
            bindPopup(popupContent);

        this.markerGroup.push(marker);
    }

    /**
     * Replace given string with
     * @param string The string to replace substrings
     * @param strings An object where properties are the strings to replace in argument string. The values
     *                are the strings to replace with.
     */
    private replaceStrings(string:string, strings:{}):string {
        for (const prop in strings) {
            if (strings.hasOwnProperty(prop)) {
                string = string.replace(prop, strings[prop]);
            }
        }
        return string;
    }


    /**
     * Get the map object (leaflet map object)
     */
    public getMapObject() {
        return this.map;
    }

}

/**
 * Define issue objects
 */
interface IssueOrOrderInMap {
    id:number,
    lat:number,     // Latitude
    lng:number,     // Longitude
    zoom:number,    // Zoom level, integer
    type:string,
    name:string,    // Name (title)
    desc:string,    // Description
    order:boolean   // is order (or issue)
}

/**
 * The configuration object to give to IssueOrderMapGUI.
 * Normally this API was used:
 * https://kartkatalog.geonorge.no/metadata/topografisk-norgeskart-cache/8f381180-1a47-4453-bee7-9a3d64843efa
 */
interface MapConfig {
    host:string,        // The hostname, example "opencache.statkart.no"
    protocol:string,    // must be "http:" or "https:"
    path:string         // The path, example "/api/map?..." with preceding slash
}